import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useWallet } from '../../context/WalletContext';

// Mock data
const mockProposals = [
  {
    id: 1,
    title: 'Reducir fees de plataforma al 2%',
    description: 'Propuesta para reducir la comisión de la plataforma del 2.5% al 2% para incentivar más subastas.',
    status: 'active',
    votesFor: 125000,
    votesAgainst: 45000,
    totalVoters: 234,
    endTime: new Date(Date.now() + 3600000 * 48),
    myVote: null,
    proposer: '0x1234...5678',
    category: 'Fees',
  },
  {
    id: 2,
    title: 'Añadir soporte para Ethereum mainnet',
    description: 'Expandir TEMPLUM a Ethereum mainnet además de Polygon para atraer más usuarios.',
    status: 'active',
    votesFor: 89000,
    votesAgainst: 112000,
    totalVoters: 189,
    endTime: new Date(Date.now() + 3600000 * 72),
    myVote: 'against',
    proposer: '0xabcd...ef01',
    category: 'Expansión',
  },
  {
    id: 3,
    title: 'Incrementar rewards de staking',
    description: 'Aumentar el APR de staking del 12% al 15% durante 6 meses.',
    status: 'passed',
    votesFor: 250000,
    votesAgainst: 50000,
    totalVoters: 456,
    endTime: new Date(Date.now() - 3600000 * 24),
    myVote: 'for',
    proposer: '0x9876...5432',
    category: 'Staking',
  },
  {
    id: 4,
    title: 'Nuevo tipo de subasta: Batch Auction',
    description: 'Implementar subastas por lotes para colecciones completas.',
    status: 'rejected',
    votesFor: 45000,
    votesAgainst: 180000,
    totalVoters: 312,
    endTime: new Date(Date.now() - 3600000 * 72),
    myVote: 'against',
    proposer: '0x5555...6666',
    category: 'Features',
  },
];

const GovernancePage: React.FC = () => {
  const { ndhBalance, tier } = useWallet();
  const [filter, setFilter] = useState('all');
  const [proposals, setProposals] = useState(mockProposals);

  // Mock voting power (staked NDH)
  const votingPower = 15000;
  const hasVotingPower = votingPower > 0;

  const handleVote = (proposalId: number, vote: 'for' | 'against') => {
    setProposals(proposals.map(p =>
      p.id === proposalId ? { ...p, myVote: vote } : p
    ));
  };

  const formatTimeRemaining = (endTime: Date) => {
    const now = new Date();
    const diff = endTime.getTime() - now.getTime();

    if (diff <= 0) return 'Finalizada';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) return `${days}d ${hours}h restantes`;
    return `${hours}h restantes`;
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'active':
        return { label: 'Activa', color: 'bg-blue-500/20 text-blue-400' };
      case 'passed':
        return { label: 'Aprobada', color: 'bg-green-500/20 text-green-400' };
      case 'rejected':
        return { label: 'Rechazada', color: 'bg-red-500/20 text-red-400' };
      default:
        return { label: 'Pendiente', color: 'bg-gray-500/20 text-gray-400' };
    }
  };

  const filteredProposals = filter === 'all'
    ? proposals
    : proposals.filter(p => p.status === filter);

  const activeProposals = proposals.filter(p => p.status === 'active');
  const myVotes = proposals.filter(p => p.myVote !== null).length;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <ShieldCheckIcon className="w-8 h-8 text-purple-400" />
            Gobernanza
          </h1>
          <p className="text-gray-400">Participa en las decisiones del DAO</p>
        </div>

        {/* Stats */}
        <div className="flex gap-4">
          <div className="bg-dark-800/50 rounded-lg px-4 py-2 border border-dark-700">
            <p className="text-xs text-gray-500">Tu Poder de Voto</p>
            <p className="text-xl font-bold text-primary-400">{votingPower.toLocaleString()}</p>
          </div>
          <div className="bg-dark-800/50 rounded-lg px-4 py-2 border border-dark-700">
            <p className="text-xs text-gray-500">Propuestas Activas</p>
            <p className="text-xl font-bold text-white">{activeProposals.length}</p>
          </div>
          <div className="bg-dark-800/50 rounded-lg px-4 py-2 border border-dark-700">
            <p className="text-xs text-gray-500">Mis Votos</p>
            <p className="text-xl font-bold text-white">{myVotes}</p>
          </div>
        </div>
      </div>

      {/* No voting power warning */}
      {!hasVotingPower && (
        <div className="mb-6 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <ShieldCheckIcon className="w-6 h-6 text-yellow-400" />
            <div>
              <p className="text-white font-medium">Necesitas stakear NDH para votar</p>
              <p className="text-gray-400 text-sm">Tu poder de voto es igual a tu NDH stakeado</p>
            </div>
            <Link
              to="/dashboard/staking"
              className="ml-auto px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 text-sm"
            >
              Ir a Staking
            </Link>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { key: 'all', label: 'Todas' },
          { key: 'active', label: 'Activas' },
          { key: 'passed', label: 'Aprobadas' },
          { key: 'rejected', label: 'Rechazadas' },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => setFilter(item.key)}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap ${
              filter === item.key
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-gray-400 hover:text-white'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Proposals List */}
      {filteredProposals.length > 0 ? (
        <div className="space-y-4">
          {filteredProposals.map((proposal, idx) => {
            const statusInfo = getStatusInfo(proposal.status);
            const totalVotes = proposal.votesFor + proposal.votesAgainst;
            const forPercentage = totalVotes > 0 ? (proposal.votesFor / totalVotes) * 100 : 0;
            const againstPercentage = totalVotes > 0 ? (proposal.votesAgainst / totalVotes) * 100 : 0;

            return (
              <motion.div
                key={proposal.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        <span className="px-2 py-0.5 bg-dark-700 text-gray-400 text-xs rounded">
                          {proposal.category}
                        </span>
                        {proposal.myVote && (
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            proposal.myVote === 'for'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            Votaste {proposal.myVote === 'for' ? 'A favor' : 'En contra'}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-white">{proposal.title}</h3>
                    </div>
                    {proposal.status === 'active' && (
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <ClockIcon className="w-4 h-4" />
                        {formatTimeRemaining(proposal.endTime)}
                      </div>
                    )}
                  </div>

                  <p className="text-gray-400 text-sm mb-4">{proposal.description}</p>

                  {/* Voting Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-green-400">A favor: {forPercentage.toFixed(1)}%</span>
                      <span className="text-red-400">En contra: {againstPercentage.toFixed(1)}%</span>
                    </div>
                    <div className="h-3 bg-dark-700 rounded-full overflow-hidden flex">
                      <div
                        className="bg-green-500 h-full"
                        style={{ width: `${forPercentage}%` }}
                      />
                      <div
                        className="bg-red-500 h-full"
                        style={{ width: `${againstPercentage}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{proposal.votesFor.toLocaleString()} NDH</span>
                      <span>{proposal.votesAgainst.toLocaleString()} NDH</span>
                    </div>
                  </div>

                  {/* Voting Buttons */}
                  {proposal.status === 'active' && !proposal.myVote && hasVotingPower && (
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleVote(proposal.id, 'for')}
                        className="flex-1 py-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <CheckCircleIcon className="w-5 h-5" />
                        A favor
                      </button>
                      <button
                        onClick={() => handleVote(proposal.id, 'against')}
                        className="flex-1 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <XCircleIcon className="w-5 h-5" />
                        En contra
                      </button>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="mt-4 pt-4 border-t border-dark-700 flex items-center justify-between text-xs text-gray-500">
                    <span>Propuesto por: {proposal.proposer}</span>
                    <span className="flex items-center gap-1">
                      <UserGroupIcon className="w-4 h-4" />
                      {proposal.totalVoters} votantes
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <DocumentTextIcon className="w-16 h-16 text-gray-700 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            No hay propuestas en esta categoría
          </h3>
          <p className="text-gray-400">
            Las propuestas aparecerán aquí cuando sean creadas
          </p>
        </div>
      )}

      {/* Info */}
      <div className="mt-8 bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <ShieldCheckIcon className="w-6 h-6 text-purple-400 flex-shrink-0" />
          <div>
            <h3 className="text-white font-semibold mb-2">Cómo funciona la gobernanza</h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>• Tu poder de voto es igual a tu NDH stakeado</li>
              <li>• Las propuestas necesitan &gt;50% de votos a favor para pasar</li>
              <li>• El período de votación es de 3-7 días según el tipo de propuesta</li>
              <li>• Cualquier holder con &gt;10,000 NDH stakeado puede crear propuestas</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GovernancePage;
